import {
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatMinutes,
  GeneralValidationErrors,
  Grid,
  GridBreak,
  IconButton,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { InfoIcon } from "components/common/InfoIcon";
import { DatePicker, DurationInput, Input, Select } from "components";
import { CopyLinkButton } from "components/common/CopyLinkButton";
import {
  LkStudentServiceDisplayFieldId,
  Permission,
  StudentServiceTemplate,
  StudentServiceTemplateAdditionalEntry,
  StudentServiceTemplateAttendee,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
} from "generated/openApi";
import { useRouter } from "hooks";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { studentLinkPageRoute } from "../StudentLinkPage/StudentLinkPage";
import { AdditionalEntries } from "./AdditionalEntries/AdditionalEntries";
import { QrCodes } from "./QrCodes";

// export type AdditionalServiceType = {
//   displayOrder: number;
//   durationInMinutes?: number | null;
//   studentServiceTypeId: number;
//   studentServiceSubTypeId?: number | null;
// };

export const StudentServiceTemplatePageBody = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  template: StudentServiceTemplate;
  finishedFetch: boolean;
  setTemplate: (value: StudentServiceTemplate) => void;
  attendanceTabs: StudentServiceTemplateAttendee[] | undefined;
  viewResource: StudentServiceTemplateViewResource;
  editResource: StudentServiceTemplateEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const selectedServiceType = props.viewResource.serviceTypes?.find((x) => x.id === props.template.serviceTypeId);
  const serviceSubTypes = props.editResource.serviceSubTypes?.filter(
    (x) => x.serviceTypeId === props.template.serviceTypeId
  );
  const generationValidationErrors = props.validationErrors[pn<StudentServiceTemplate>("generalValidationErrors")];
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [currentTab, setCurrentTab] = useState<StudentServiceTemplateAttendee>();
  const [additionalTypes, setAdditionalTypes] = useState<StudentServiceTemplateAdditionalEntry[]>([]);
  const [linkCopy, setLinkCopy] = useState<boolean>(false);
  const [splitDuration, setSplitDuration] = useState(false);

  const [debounce1, setDebounce1] = useState(false);
  const [debounce2, setDebounce2] = useState(false);

  const displayFields = props.viewResource.displayFields || [];

  const languageDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Language);
  const modalityDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Modality);
  const splitDurationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.SplitDuration);
  const serviceLocationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.ServiceLocation);

  useEffect(() => {
    updateSplitDurations();
  }, [splitDuration]);

  useEffect(() => {
    if (props.attendanceTabs && props.attendanceTabs.length > 0) {
      setCurrentTab(props.attendanceTabs[0]);
    }
  }, [props.attendanceTabs]);


  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const debounceUseEffect = async () => {
      if (props.template.additionalStudentServiceTypes && props.template.additionalStudentServiceTypes != '[]') {
        setAdditionalTypes(JSON.parse(props.template.additionalStudentServiceTypes));
        await sleep(1200);
        setDebounce1(false);
      }
      else {
        await sleep(1200);
        setDebounce1(false);
      }
    }

    if (!debounce1 && props.finishedFetch) {
      setDebounce1(true);
      debounceUseEffect();
    }
  }, [props.template]);


  useEffect(() => {
    const debounceUseEffect2 = async () => {
      props.setTemplate(
        produce(props.template, (draft) => {
          draft.additionalStudentServiceTypes = JSON.stringify(additionalTypes);
        })
      );

      await sleep(1200);
      setDebounce2(false);
    }

    if (!debounce2 && props.finishedFetch) {
      setDebounce2(true);
      debounceUseEffect2();
    }
  }, [additionalTypes]);

  const splitDurationField = () => {
    return (
      <Checkbox label="Split Duration" checked={splitDuration} onChange={(e) => setSplitDuration(!splitDuration)} />
    );
  };

  const updateSplitDurations = () => {
    if (splitDuration && props.template.durationInMinutes != null) {
      if (additionalTypes != undefined && additionalTypes.length > 0) {
        let splitAmount = (props.template.durationInMinutes / (additionalTypes.length + 1));
        splitAmount = Math.ceil(Math.round(splitAmount * 100) / 100);

        setAdditionalTypes(
          produce(additionalTypes, (draft) => {
            draft.forEach((entry) => {
              entry.durationInMinutes = splitAmount!;
            });
          })
        );

        props.setTemplate(
          produce(props.template, (draft) => {
            draft.durationInMinutes = splitAmount!;
          })
        );
      }
    }
  }

  const dateField = () => {
    const propertyName = pn<StudentServiceTemplate>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.template.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.date}
        onChange={(value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" spacing="sm" alignItems="center">
          <Typography variant="inherit">{props.template.serviceType?.name}</Typography>
          {props.template.serviceType?.isForParent && <EscalatorWarningIcon />}
        </Stack>
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.serviceTypes?.find((x) => x.id === props.template.serviceTypeId)}
        options={props.viewResource.serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.serviceSubType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.template.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentServiceTemplate>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.parentCount}</DataDisplay>
    ) : (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Input
          modelProperty={property}
          required
          errors={props.validationErrors[propertyName]}
          value={props.template.parentCount}
          onChange={(e) =>
            props.setTemplate(
              produce(props.template, (draft) => {
                draft.parentCount = parseFloat(e.target.value);
              })
            )
          }
        />
        <InfoIcon text="Number of Parents Per Student" />
      </Stack>
    );
  };

  const nameField = () => {
    const propertyName = pn<StudentServiceTemplate>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.name}</DataDisplay>
    ) : (
      <Select
        freeSolo
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        inputValue={props.template.name}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.name = value?.name;
            })
          )
        }
        onInputChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.studentServiceNames(searchString)}
      />
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<StudentServiceTemplate>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.template.durationInMinutes)}</DataDisplay>
    ) : (
      <DurationInput
        required
        errors={props.validationErrors[propertyName]}
        value={props.template.durationInMinutes}
        onBlur={() => updateSplitDurations()}
        onChange={(value) => {
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.durationInMinutes = value || undefined;
            })
          )
        }
        }
      />
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentServiceTemplate>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.template.colleges?.map((x) => x.name!)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.colleges}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentServiceTemplate>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.template.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const providersField = () => {
    const propertyName = pn<StudentServiceTemplate>("providers");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.template.providers?.map((x) => `${x.lastName}, ${x.firstName}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.providers}
        options={props.editResource.providers}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.providers = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const languageField = () => {
    const propertyName = pn<StudentServiceTemplate>("languageId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.language?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.languages?.find((x) => x.id === props.template.languageId)}
        options={props.editResource.languages}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.languageId = value?.id;
            })
          )
        }
      />
    );
  };

  const modalityField = () => {
    const propertyName = pn<StudentServiceTemplate>("modalityId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.modality?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.modalities?.find((x) => x.id === props.template.modalityId)}
        options={props.editResource.modalities}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.modalityId = value?.id;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentServiceTemplate>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.template.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.note}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const renderAttendance = () => {
    return (
      <Grid container spacing="sm">
        <Grid item xs={2}>
          <Select
            label="Check-In Date"
            value={currentTab}
            options={props.attendanceTabs}
            getOptionLabel={(option) => option.label || ""}
            onChange={(e, value) =>
              setCurrentTab(value)
              // props.setTemplate(
              //   produce(props.template, (draft) => {
              //     draft.serviceTypeId = value?.id;
              //     draft.serviceSubTypeId = undefined;
              //     draft.parentCount = undefined;
              //   })
              // )
            }
          />
          {/* <TabContext value={currentTab?.label || ""}>
            <Typography textAlign={"center"} variant="body1">
              Check-In Date
            </Typography>
            <Tabs
              orientation="vertical"
              onChange={(e, value) => setCurrentTab(value)}
              value={currentTab}
              variant="standard"
              scrollButtons
              allowScrollButtonsMobile
            >
              {props.attendanceTabs?.map((tab) => (
                <Tab key={tab.label} label={tab.label} value={tab} />
              ))}
            </Tabs>
          </TabContext> */}
        </Grid>

        <GridBreak />

        <Grid item xs={12}>
          {currentTab && (
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Grid container spacing="lg">
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">
                      {props.viewResource.customIdTypes && props.viewResource.customIdTypes.length > 0
                        ? props.viewResource.customIdTypes![0].name
                        : "Student ID"}
                    </Typography>
                  </Grid>
                  {props.template.enableAdditionalServiceSelect && (
                    <Grid item xs={4}>
                      <Typography variant="body1">Services Attended</Typography>
                    </Grid>
                  )}
                  <Grid item xs={2}>
                    <Typography variant="body1">Student</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Check-In</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Check-Out</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1">Duration (min.)</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Unknown Student</Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentTab.studentServices?.map((student) => (
                <Grid item xs={12}>
                  <Grid container spacing="lg">
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.customId}</Typography>
                    </Grid>
                    {props.template.enableAdditionalServiceSelect && (
                      <Grid item xs={4}>
                        <Typography variant="body1">{student.servicesAttended}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={2}>
                      <Typography variant="body1">{student.studentName}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.startTime || "---"}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.endTime || "---"}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body1">{student.durationInMinutes || "---"}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      {student.isPartial && student.partialStudentId && (
                        <IconButton
                          onClick={() =>
                            navigate({
                              pathname: studentLinkPageRoute.getPath({
                                studentPartialId: student.partialStudentId!,
                                screenType: "dialog",
                              }),
                            })
                          }
                        >
                          <PriorityHighIcon sx={{ color: "red" }} />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const requireSignatureField = () => {
    const propertyName = pn<StudentServiceTemplate>("requireSignature");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Enables collection of digital signature." />    </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.requireSignature)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.requireSignature}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.requireSignature = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isActiveField = () => {
    const propertyName = pn<StudentServiceTemplate>("isActive");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Keeps the QR Code active." />
      </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.isActive)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.isActive}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.isActive = e.target.checked;
            })
          )
        }
      />
    );
  };

  const defaultAllField = () => {
    const propertyName = pn<StudentServiceTemplate>("enableAdditionalServiceSelect");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Allows participant to select what service they are attending." />
      </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.enableAdditionalServiceSelect)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.enableAdditionalServiceSelect}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.enableAdditionalServiceSelect = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <>
      <Stack spacing="lg">
        <Card header="Info">
          <Grid ref={breakpointsRef} container spacing="sm">
            {generationValidationErrors && (
              <Grid item xs={12}>
                <GeneralValidationErrors errors={generationValidationErrors} />
              </Grid>
            )}

            {props.template.id != 0 && props.template.validationToken != null && (
              <Grid item xs={12}>
                <Grid direction="row" container spacing="lg">
                  <Grid item xs={11}>
                    <DataDisplay label="Public Link">{`${props.viewResource?.qrCodeUrl}`}</DataDisplay>
                  </Grid>
                  <Grid item xs={1}>
                    <CopyLinkButton
                      copyLink={`${props.viewResource?.qrCodeUrl}`}
                      showCheck={linkCopy}
                      setShowCheck={setLinkCopy}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {dateField()}
            </Grid>

            <GridBreak />

            <Grid item xs={smallFieldBreakpoints}>
              {serviceTypeField()}
            </Grid>

            {serviceSubTypes && serviceSubTypes.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {serviceSubTypeField()}
              </Grid>
            )}

            {/* {selectedServiceType?.isForParent && (
              <Grid item xs={smallFieldBreakpoints}>
                {parentCountField()}
              </Grid>
            )} */}

            <Grid item xs={smallFieldBreakpoints}>
              {durationInMinutesField()}
            </Grid>

            {splitDurationDisplayField && additionalTypes != undefined && additionalTypes?.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {splitDurationField()}
              </Grid>
            )}

            {serviceLocationDisplayField && (
              <>
                {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
                  <Grid item xs={mediumFieldBreakpoints}>
                    {collegesField()}
                  </Grid>
                ) : (
                  <Grid item xs={smallFieldBreakpoints}>
                    {schoolField()}
                  </Grid>
                )}
              </>
            )}

            <GridBreak />

            <Grid item xs={12}>
              <AdditionalEntries
                template={props.template}
                crudMode={props.crudMode}
                rootPermission={props.rootPermission}
                entries={additionalTypes || []}
                setEntries={(values) => {
                  setAdditionalTypes(values);
                }}
                setEditMode={() => { }}
                viewResource={props.viewResource}
                editResource={props.editResource}
                validationErrors={props.validationErrors[pn<StudentServiceTemplate>("additionalStudentServiceTypes")] || {}}
              />
            </Grid>

            <GridBreak />

            <Grid item xs={smallFieldBreakpoints}>
              {nameField()}
            </Grid>

            <Grid item xs={largeFieldBreakpoints}>
              {providersField()}
            </Grid>

            {languageDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {languageField()}
              </Grid>
            )}

            {modalityDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {modalityField()}
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {requireSignatureField()}
            </Grid>

            <Grid item xs={smallFieldBreakpoints}>
              {isActiveField()}
            </Grid>

            {additionalTypes && additionalTypes.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {defaultAllField()}
              </Grid>
            )}

            <Grid item xs={fullFieldBreakpoints}>
              {noteField()}
            </Grid>

            <GridBreak />
          </Grid>
        </Card>

        {props.template && props.template.id > 0 && (
          <QrCodes template={props.template} setTemplate={props.setTemplate} />
        )}

        <Grid item xs={smallFieldBreakpoints}>
          <Card header="Attendance">{renderAttendance()}</Card>
        </Grid>
      </Stack>

      {matchTemplatePath(studentLinkPageRoute.templatePath) && <Outlet />}
    </>
  );
};
